import { useEffect, useRef, useState } from 'react'
import { BookOpen, CalendarDays, Inbox, LifeBuoy, Mail, Menu, Store, Users, X } from 'lucide-react'
import type { TopBarProps } from '../types'
import { track } from '../lib/analytics'
import type { RouteName } from '../lib/route'
import { BrandMark } from './BrandMark'

// The page links share this. Groups used to be excluded, because it opened a
// modal and so was never "the page you are on"; it is a route now, so it
// highlights like the rest. Active is white on the paper ground, which is the
// same card-lifts-off-paper move the rest of the app makes, plus a hairline ring
// so it stays distinct from the hover state.
// px-1 below sm, where the bar is 16px short of fitting its own contents: at
// 375px the left group wanted 258px and had 247, so the tail of "Stories" was
// painted underneath the Subscribe button. Eight side-paddings are the
// cheapest 16px in the header - the pill still clears its label, and min-h-10
// means the tap target never depended on the padding in the first place.
// Tightened again from px-1.5 when Vendors became a fourth item (8/19); still
// wants a live check at 320-390px, since nothing here has run in a browser.
// From sm the measure is back.
const NAV =
  'flex min-h-10 shrink-0 items-center justify-center rounded-md px-1 py-1.5 text-sm font-medium transition-colors sm:min-h-0 sm:px-2.5'
const NAV_ON = `${NAV} bg-white text-brick-700 ring-1 ring-hairline dark:bg-slate-800 dark:text-brick-300 dark:ring-slate-700`
const NAV_OFF = `${NAV} text-slate-700 transition-colors hover:bg-white dark:text-slate-200 dark:hover:bg-slate-800`

// onOpenStories and the routing props ride alongside TopBarProps rather than
// being added to it: SubscribeModal already sets the precedent for a component
// declaring its own props locally, and types.ts is owned elsewhere this week.
export function TopBar(
  props: TopBarProps & {
    onOpenStories: () => void
    onOpenCalendar: () => void
    onOpenVendors: () => void
    onOpenDisasterResources: () => void
    // Callback ref for the phone-only slot the calendar's own controls portal
    // into. See CalendarView: the controls keep their state and their JSX, they
    // just render into this row instead of a second bar underneath it.
    controlsSlotRef: (el: HTMLDivElement | null) => void
    route: RouteName
  },
) {
  const {
    onOpenGroups,
    onOpenStories,
    onOpenCalendar,
    onOpenVendors,
    onOpenDisasterResources,
    controlsSlotRef,
    route,
    isAdmin,
    pendingCount,
    onOpenApproval,
    userEmail,
    onSignOut,
    onOpenSubscribe,
  } = props
  // The phone menu. On a phone the subscribe button is gone from this bar (it is
  // a bottom tab now), and the space it freed carries the two destinations that
  // do not belong in a four-item nav row: Vendors, and the disaster resources
  // page that used to be a footer link nobody scrolls to.
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const menuItem =
    'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-paper dark:text-slate-200 dark:hover:bg-slate-800'

  // onOpenSubmit and onOpenLogin stay on TopBarProps but are unused here for now:
  // "Submit event" moved to the calendar header, and the admin sign-in button is
  // removed. Keeping them on the type means restoring either is a one-line change.

  return (
    <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-hairline bg-paper px-2 sm:px-4 dark:border-slate-800 dark:bg-slate-950">
      {/* LEFT: the brand lockup, then the page links. The lockup links out to the
          newsletter, which is the thing the calendar is a companion to.
          gap-4 at minimum because the kit asks for clear space of half the tile
          height around the mark - 16px on a 32px tile - and nothing may intrude. */}
      {/* gap-2 on phones. Four items plus the subscribe CTA do not fit a 380px
          bar at the kit's 16px clear space, and the space between links is the
          cheapest thing to give up - shrinking the mark or dropping a link both
          cost more. From sm the kit's measure is back. */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-6">
        <a
          href="/"
          onClick={(e) => {
            track('wordmark_clicked')
            // Same-tab, in-app navigation to the calendar root (Lisa, 8/19: it
            // used to point out to lyhevents.com in a new tab, which surprised
            // people who just wanted back to the calendar - wrong on desktop too,
            // not only mobile). A modifier or non-primary click still gets the
            // browser's own new-tab/new-window behaviour on this same href; a
            // plain click stays in this tab and goes through the SPA's own
            // router, which is a no-op when already on the calendar.
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
            e.preventDefault()
            onOpenCalendar()
          }}
          title="LYH Events calendar"
          // gap-2 is the kit's horizontal lockup measure: a quarter of the tile
          // width, so 8px against the 32px tile.
          // shrink-0, or the lockup is what flex gives up when the row is full:
          // measured at 380px the mark itself had been clipped to 7px of its 32.
          className="flex shrink-0 items-center gap-2 text-slate-900 transition-colors hover:text-brick-600 dark:text-slate-50 dark:hover:text-brick-300"
        >
          {/* Two marks rather than one responsive one: the letters differ only
              below md, and rendering both keeps the desktop bar byte-identical
              to what the kit signed off. */}
          <BrandMark size={32} letterSize={20.25} className="shrink-0 md:hidden" />
          <BrandMark size={32} className="hidden shrink-0 md:block" />
          {/* Fraunces is variable, and the kit is explicit that the wonk and the
              softness ARE the brand - left at 0 it renders as generic Georgia.
              Tailwind has no utility for font-variation-settings, so it rides
              inline. opsz 72 is the kit's display setting, against the tile's 144. */}
          {/* The mark stands alone below sm. Adding Calendar put four items in a
              390px bar, and measured there the wordmark was squeezed from its
              natural 89px to ZERO - the truncate left an empty box where the
              brand should be. Dropping the text on phones frees 97px, which is
              more than Calendar costs, and the kit already allows the tile by
              itself. Above sm the full lockup is back. */}
          <span
            className="hidden truncate font-display text-lg font-semibold tracking-tight sm:inline"
            style={{ fontVariationSettings: "'opsz' 72, 'SOFT' 40, 'WONK' 1" }}
          >
            LYH Events
          </span>
        </a>

        {/* The three page links are their own row inside the left group, so the
            space BETWEEN them can be tightened on a phone without touching the
            space around the mark. The kit's clear space belongs to the mark and
            is already down to its floor at 8px; the links owe each other
            nothing, so gap-0.5 there buys 12px that would otherwise have to come
            out of the brand. From sm both are the kit's gap-6 again, so nothing
            about the desktop bar changes. */}
        {/* Below md this whole row moves into the menu button on the right
            (Andrew, 2026-09-07), which frees the width the calendar's own
            controls now use. Desktop is unchanged. */}
        <div className="hidden items-center gap-0 md:flex sm:gap-6">
          {/* Calendar comes first, and it exists for the way OUT of Your Stories
              (Lisa, 7/28). From that page the only route back was the wordmark,
              which links to the newsletter on every other site in the world, so
              nothing signalled it. */}
          <button
            type="button"
            onClick={() => {
              track('nav_calendar_clicked', { from: route })
              onOpenCalendar()
            }}
            title="Calendar"
            aria-current={route === 'calendar' ? 'page' : undefined}
            className={route === 'calendar' ? NAV_ON : NAV_OFF}
          >
            Calendar
          </button>

          <button
            type="button"
            onClick={() => {
              track('groups_opened', { from: route })
              onOpenGroups()
            }}
            title="Groups"
            aria-current={route === 'groups' ? 'page' : undefined}
            className={route === 'groups' ? NAV_ON : NAV_OFF}
          >
            Groups
          </button>

          {/* Vendors sits right after Groups (Lisa, 8/19): the calendar is also
              a resource for the OTHER side of an event, the makers, food trucks
              and artisans looking for somewhere to sell, and the hosts looking
              for them. This is the fourth item in a bar three already fought
              for room in - "Vendors" alone on a phone is the same length as
              "Calendar", so it costs no more than an average nav word, but a
              live check at 320-390px is still owed (see the PR's click-through
              checklist). */}
          <button
            type="button"
            onClick={() => {
              track('nav_vendors_clicked', { from: route })
              onOpenVendors()
            }}
            title="Hosts & Vendors"
            aria-current={route === 'vendors' ? 'page' : undefined}
            // Hidden below md: it moved into the phone menu button on the right.
            // Four nav items plus a CTA never fit this bar - the comment above
            // and the 320px measurements in it are the history of trying - so
            // the fourth one stops competing for the row and becomes a menu item.
            className={`hidden md:inline-flex ${route === 'vendors' ? NAV_ON : NAV_OFF}`}
          >
            <span className="sm:hidden">Vendors</span>
            <span className="hidden sm:inline">Hosts &amp; Vendors</span>
          </button>

          <button
            type="button"
            onClick={onOpenStories}
            title="Your Stories"
            aria-current={route === 'stories' ? 'page' : undefined}
            className={route === 'stories' ? NAV_ON : NAV_OFF}
          >
            {/* "Stories" on a phone, where this now sits beside Calendar, Groups,
                Vendors and the subscribe button in a 56px-tall bar. */}
            <span className="sm:hidden">Stories</span>
            <span className="hidden sm:inline">Your Stories</span>
          </button>
        </div>

        {/* The Insights button ("What people want more of") was removed 7/28, and
            its modal, the WishlistPanel it wrapped and their data were removed
            from the repo on 2026-10-10. */}
      </div>

      {/* RIGHT: subscribe button + admin. "Submit event" now lives next to the
          week/month toggle in the calendar header. The desktop inline email form
          is gone (Andrew, 7/28) - one button at every width opens the subscribe
          modal, which now sells the newsletter rather than just asking. */}
      {/* The calendar's controls land here on a phone, in the space Calendar /
          Groups / Stories vacated. min-w-0 so a long date label truncates rather
          than pushing the menu button off the bar. */}
      {/* ml-[2px] on top of the header's gap-2 leaves 10px between the mark and
          the Filters button (Andrew, 2026-09-07: 5px first, then "5px more").
          The header gap itself has to stay 8, because it also sets the space
          before the menu button on the other side. */}
      <div
        ref={controlsSlotRef}
        className="ml-[2px] flex min-w-0 flex-1 items-center md:hidden"
      />

      <div className="flex min-w-0 shrink items-center justify-end gap-1 sm:gap-2">
        {/* The phone menu button. It replaces the subscribe CTA in this slot,
            because subscribing is a bottom tab now and this bar has spent three
            rounds of comments fighting for width it does not have. Vendors and
            the disaster resources page live behind it. */}
        <div className="relative md:hidden" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            className="flex h-10 w-10 items-center justify-center rounded-md text-slate-700 transition-colors hover:bg-white dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-full z-50 mt-1 w-52 overflow-hidden rounded-lg border border-hairline bg-white shadow-lg dark:border-slate-800 dark:bg-slate-900"
            >
              <button
                type="button"
                role="menuitem"
                aria-current={route === 'calendar' ? 'page' : undefined}
                onClick={() => {
                  track('nav_calendar_clicked', { from: route })
                  setMenuOpen(false)
                  onOpenCalendar()
                }}
                className={menuItem}
              >
                <CalendarDays className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
                Calendar
              </button>

              <button
                type="button"
                role="menuitem"
                aria-current={route === 'groups' ? 'page' : undefined}
                onClick={() => {
                  track('groups_opened', { from: route })
                  setMenuOpen(false)
                  onOpenGroups()
                }}
                className={menuItem}
              >
                <Users className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
                Groups
              </button>

              <button
                type="button"
                role="menuitem"
                aria-current={route === 'stories' ? 'page' : undefined}
                onClick={() => {
                  setMenuOpen(false)
                  onOpenStories()
                }}
                className={menuItem}
              >
                <BookOpen className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
                Your Stories
              </button>

              <div className="my-1 border-t border-hairline dark:border-slate-800" />

              <button
                type="button"
                role="menuitem"
                aria-current={route === 'vendors' ? 'page' : undefined}
                onClick={() => {
                  track('nav_vendors_clicked', { from: route })
                  setMenuOpen(false)
                  onOpenVendors()
                }}
                className={menuItem}
              >
                <Store className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
                Hosts &amp; Vendors
              </button>

              {/* Was a footer link, which on a phone meant scrolling the whole
                  calendar to find it. Still deliberately not a headline item -
                  it is a page for a bad day - but one tap from anywhere now. */}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  track('disaster_resources_opened', { from: 'top_bar_menu' })
                  setMenuOpen(false)
                  onOpenDisasterResources()
                }}
                className={menuItem}
              >
                <LifeBuoy className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" />
                Disaster resources
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onOpenSubscribe}
          title="Subscribe to the newsletter"
          className="hidden min-h-10 shrink-0 items-center justify-center gap-1.5 rounded-md bg-brick-600 px-2.5 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brick-700 md:flex md:min-h-0"
        >
          {/* Between 360 and sm the icon goes rather than the word: it's
              decoration on a filled brick button that already says Subscribe,
              and dropping it pays 22px of the 16px the bar was short.

              Under 360 they swap - the word goes and the icon is the button.
              Nothing else in the header is compressible by then, and 320px is
              not a rounding error: PostHog has 11 sessions at exactly 320 in the
              last 30 days, against 18 at 360. Every one of those was reading a
              nav label with a button sitting on top of it. */}
          <Mail className="h-4 w-4 min-[360px]:hidden sm:block" />
          <span className="hidden min-[360px]:inline md:hidden">Subscribe</span>
          <span className="hidden md:inline">Subscribe to the newsletter</span>
        </button>

        {isAdmin ? (
          <>
            <button
              type="button"
              onClick={onOpenApproval}
              title="Approval queue"
              className="relative flex min-h-10 min-w-10 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-white sm:min-h-0 sm:min-w-0 sm:px-2.5 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <Inbox className="h-4 w-4" />
              <span className="hidden sm:inline">Approval queue</span>
              {pendingCount > 0 && (
                <span className="ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-rose-600 px-1.5 text-xs font-semibold text-white">
                  {pendingCount}
                </span>
              )}
            </button>

            <div className="flex items-center gap-2 pl-1">
              {userEmail && (
                <span className="hidden max-w-[12rem] truncate text-xs text-slate-500 sm:inline dark:text-slate-400">
                  {userEmail}
                </span>
              )}
              <button
                type="button"
                onClick={onSignOut}
                className="rounded-md px-2 py-1.5 text-sm font-medium text-slate-500 transition-colors hover:bg-white hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                Sign out
              </button>
            </div>
          </>
        ) : null /* Admin sign-in button removed for now (Andrew, 7/21). Sign in by
                    calling onOpenLogin from the console, or restore this button. */}
      </div>
    </header>
  )
}
